import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ArrowLeft, MessageCircle, Share2, Bell, BellOff, MoreHorizontal, Ban, Copy, X, Search, Plus, Settings, Image as ImageIcon, Users, Lock, FileText, MapPin, Link, Check, GraduationCap, Clock } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { ScrollArea } from './ui/scroll-area';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from './ui/dropdown-menu';
import { toast } from 'sonner';
import { useYapInteractions } from './YapInteractionsContext';
import { JoinRequestsModal } from './JoinRequestsModal';
import { YapVideoPlayer } from './ui/YapVideoPlayer';
import { YapImageGrid } from './ui/YapImageGrid';
import { PressableCard } from './ui/PressableCard';
import { formatMemberCount } from './utils/memberCount';
import { YapTopicLabel } from './ui/YapTopicLabel';
import { FriendBadge } from './ui/FriendBadge';
import { useUserData } from './UserDataContext';
import { generateInviteCode, buildTribeInviteLink } from '../utils/inviteCode';
import { useContentFilter } from '../hooks/useContentFilter';
import { ShareYapModal } from './ShareYapModal';
import { ShareGroupModal } from './ShareGroupModal';
import { ParallaxBackground } from './utils/parallax';
import { TribeService, UserService } from '../services/api';
import { GovernanceService } from '../services/GovernanceService';
import { Yap, UserRank, Tribe, User } from '../types';
import { GroupSettingsModal } from './GroupSettingsModal';
import { getRankRingParams, getRankBadge } from './utils/rankUtils';
import RankBadge from './RankBadge';
import { useTheme } from './ThemeProvider';
import { getGroupGradient } from './utils/groupUtils';
import { FormattedText } from './FormattedText';
import { PollCard } from './ui/PollCard';

import { FlagImage } from './utils/countryFlags';
import { PostMenu } from './ui/PostMenu';
import { CommunityRulesModal } from './CommunityRulesModal';
import { IdentityTag } from './IdentityTag';
import { GroupMembersModal } from './GroupMembersModal';
import { useScreenReady } from '../contexts/ScreenTransitionContext';
import { yapCharLimit, exceedsYapLimit, YAP_MAX_CHARACTERS } from '../constants/posts';
import { ExpandableText } from './ui/ExpandableText';


interface TribeGroupScreenProps {
  tribe: any;
  onBack: () => void;
  initialInviteCode?: string;
}

export function TribeGroupScreen({ tribe: initialTribe, onBack, initialInviteCode }: TribeGroupScreenProps) {
  const { userProfile, firebaseUser, joinTribe, leaveTribe, isTribeMember, isFriend } = useUserData();
  const { isBlockedContent } = useContentFilter();
  const { theme } = useTheme();
  const [tribe, setTribe] = useState(initialTribe);
  const [isJoined, setIsJoined] = useState(false);
  const [myGroupRole, setMyGroupRole] = useState<string>('member'); // group-scoped role
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [selectedYap, setSelectedYap] = useState<any>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showNewPostModal, setShowNewPostModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [ownerInfo, setOwnerInfo] = useState<User | null>(null);
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [newPostContent, setNewPostContent] = useState('');
  const [newPostImages, setNewPostImages] = useState<string[]>([]);
  const [discussions, setDiscussions] = useState<Yap[]>([]);
  const [loading, setLoading] = useState(true);
  useScreenReady(!loading);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [isTitleExpanded, setIsTitleExpanded] = useState(false);
  const [showRulesModal, setShowRulesModal] = useState(false);
  const [showMembersModal, setShowMembersModal] = useState(false);
  const [joinRequestPending, setJoinRequestPending] = useState(false);
  const [showJoinRequestsModal, setShowJoinRequestsModal] = useState(false);
  // Real pending join-request count, derived from the join_requests docs (the
  // tribe.pendingMemberCount counter can be stale/0). Drives the owner badge.
  const [pendingRequestCount, setPendingRequestCount] = useState(0);
  const [showEmailPrompt, setShowEmailPrompt] = useState(false);
  const [joinEmail, setJoinEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [requestSubmitting, setRequestSubmitting] = useState(false);
  const [inviteCopied, setInviteCopied] = useState(false);
  const [generatingInvite, setGeneratingInvite] = useState(false);

  // Mint an invite code for a tribe that doesn't have one yet and persist it, so
  // the link keeps working after a reload and for everyone the owner shares it
  // with. Owners can write the tribe doc directly (firestore.rules), so no
  // Cloud Function is needed.
  const handleGenerateInviteLink = async () => {
    if (!tribe?.id || generatingInvite) return;
    setGeneratingInvite(true);
    try {
      const code = generateInviteCode();
      await TribeService.updateTribe(tribe.id, { inviteCode: code } as any);
      setTribe((prev: any) => ({ ...prev, inviteCode: code }));
      const link = buildTribeInviteLink(tribe.id, code);
      try {
        await navigator.clipboard.writeText(link);
        toast.success('Invite link created and copied');
      } catch {
        toast.success('Invite link created');
      }
    } catch (e) {
      console.error('[TribeGroup] invite link generation failed:', e);
      toast.error('Could not create the invite link');
    } finally {
      setGeneratingInvite(false);
    }
  };
  const imageInputRef = useRef<HTMLInputElement>(null);
  const inviteAutoTriggeredRef = useRef(false);
  // Server-side invite token validation (graceful: invalid never blocks the page).
  const [inviteCheck, setInviteCheck] = useState<
    { status: 'checking' | 'valid' | 'invalid'; tribeName: string; requiresApproval: boolean } | null
  >(null);

  useEffect(() => {
    if (!initialInviteCode) { setInviteCheck(null); return; }
    let cancelled = false;
    setInviteCheck({ status: 'checking', tribeName: initialTribe?.name || 'this tribe', requiresApproval: !!initialTribe?.requiresApproval });
    (async () => {
      try {
        const { getFunctions, httpsCallable } = await import('firebase/functions');
        const { getApp } = await import('firebase/app');
        const fn = httpsCallable(getFunctions(getApp()), 'validateTribeInvite');
        const res: any = await fn({ tribeId: initialTribe.id, inviteCode: initialInviteCode });
        if (cancelled) return;
        const d = res?.data || {};
        setInviteCheck({
          status: d.valid ? 'valid' : 'invalid',
          tribeName: d.tribeName || initialTribe?.name || 'this tribe',
          requiresApproval: !!d.requiresApproval,
        });
      } catch {
        // Validation failed → degrade gracefully (still allow request to join).
        if (!cancelled) setInviteCheck({ status: 'invalid', tribeName: initialTribe?.name || 'this tribe', requiresApproval: !!initialTribe?.requiresApproval });
      }
    })();
    return () => { cancelled = true; };
  }, [initialInviteCode, initialTribe?.id]);

  // Auto-open join prompt when arriving via invite link
  useEffect(() => {
    if (!initialInviteCode || inviteAutoTriggeredRef.current) return;
    if (isJoined || joinRequestPending) return;
    if (!tribe.requiresApproval && tribe.tribeType !== 'university') return;
    inviteAutoTriggeredRef.current = true;
    setJoinEmail('');
    setEmailError('');
    setShowEmailPrompt(true);
  }, [initialInviteCode, isJoined, joinRequestPending, tribe.requiresApproval, tribe.tribeType]);

  const currentUserId = userProfile?.id || firebaseUser?.uid;
  const isOwner = tribe.ownerId && currentUserId && (
    String(tribe.ownerId).trim() === String(currentUserId).trim() || 
    (userProfile?.originalId && String(tribe.ownerId).trim() === String(userProfile.originalId).trim())
  );

  // Load posts
  useEffect(() => {
    const loadPosts = async () => {
      if (tribe && tribe.id) {
        setLoading(true);

        // Ensure tribe document exists in Firestore (for count tracking)
        try {
          const { getDbSafe } = await import('../services/firebaseConfig');
          const { doc, getDoc, setDoc, serverTimestamp, collection, getCountFromServer } = await import('firebase/firestore');
          const db = await getDbSafe();
          const tribeRef = doc(db, "tribes", tribe.id);
          const tribeSnap = await getDoc(tribeRef);

          // Count members from the subcollection — the denormalized memberCount
          // field on the tribe document drifts (and is 1 for any freshly-
          // initialized doc), so reading directly from members/ is the only
          // way to get the true count. getCountFromServer is an aggregate that
          // doesn't pull doc bodies, so it's cheap.
          let liveMemberCount: number | null = null;
          try {
            const membersSnap = await getCountFromServer(collection(db, 'tribes', tribe.id, 'members'));
            liveMemberCount = membersSnap.data().count;
          } catch (countErr) {
            console.warn('[TribeGroup] Failed to count members subcollection:', countErr);
          }
          
          let effectiveOwnerId = tribe.ownerId;

          if (tribeSnap.exists()) {
            const tribeData = tribeSnap.data();
            // 🛡️ SYNC: Pull core data (including banners) from Firestore document.
            // Firestore is the source of truth for ownerId — prefer it over the
            // (possibly stale) prop so a passed-down ownership change is reflected
            // and the previous owner is correctly demoted to a regular member.
            // Fall back to the prop only when the doc has no ownerId yet.
            if (tribeData.ownerId) {
              effectiveOwnerId = tribeData.ownerId;
            }
            // Update state so the UI instantly reflects any new banners/details
            setTribe((prev: any) => ({
               ...prev,
               ownerId: effectiveOwnerId,
               bannerUrl: tribeData.bannerUrl || tribeData.bannerImage || tribeData.image || prev.bannerUrl || '',
               image: tribeData.image || prev.image || '',
               bannerImage: tribeData.bannerImage || prev.bannerImage || '',
               name: tribeData.name || prev.name,
               description: tribeData.description !== undefined ? tribeData.description : prev.description,
               // Member count: take the MAX of all signals so we never underreport.
               // joinTribe writes to BOTH the legacy `members` array on the doc
               // AND the `tribes/{id}/members` subcollection, plus increments the
               // denormalized memberCount counter — but historic joins may exist
               // in only one source, so picking the largest is the safest bet.
               memberCount: Math.max(
                 Array.isArray(tribeData.members) ? tribeData.members.length : 0,
                 typeof tribeData.memberCount === 'number' ? tribeData.memberCount : 0,
                 liveMemberCount ?? 0,
               ) || prev.memberCount,
               postCount: tribeData.postCount ?? prev.postCount,
               // Phase 3: pull approval fields from Firestore
               requiresApproval: tribeData.requiresApproval ?? prev.requiresApproval ?? false,
               pendingMemberCount: tribeData.pendingMemberCount ?? prev.pendingMemberCount ?? 0,
               // University tribe fields
               tribeType: tribeData.tribeType ?? prev.tribeType,
               schoolName: tribeData.schoolName ?? prev.schoolName,
               schoolLogoUrl: tribeData.schoolLogoUrl ?? prev.schoolLogoUrl,
               hiddenOwner: tribeData.hiddenOwner ?? prev.hiddenOwner ?? false,
               inviteCode: tribeData.inviteCode ?? prev.inviteCode,
            }));
          } else {
            console.log(`[TribeGroup] Initializing tribe document for: ${tribe.id}`);
            await setDoc(tribeRef, {
              name: tribe.name,
              description: tribe.description || '',
              bannerUrl: tribe.bannerUrl || '',
              postCount: 0,
              memberCount: 1, // Start with owner if new? Or 0. Safe to assume retrieved tribe obj has count.
              ownerId: tribe.ownerId || null,
              createdAt: serverTimestamp(),
              type: 'tribe'
            }, { merge: true });
          }

          // Always reflect the live member count in state, even when the doc
          // didn't exist beforehand — otherwise the header reads the stale
          // value from the discover card.
          if (liveMemberCount !== null) {
            setTribe((prev: any) => ({ ...prev, memberCount: liveMemberCount }));
          }

          // Fetch owner info
          if (effectiveOwnerId) {
            try {
              const owner = await UserService.fetchUserById(effectiveOwnerId);
              if (owner) {
                setOwnerInfo(owner);
              }
            } catch (e) {
              console.error("Failed to fetch tribe owner:", e);
            }
          } else {
             // 🏛️ SYSTEM FALLBACK
             setOwnerInfo({ name: "AfroYap Team", username: "afroyapp" } as User);
          }
        } catch (e) {
          console.error("Failed to initialize tribe document:", e);
        }

        const posts = await TribeService.getPosts(tribe.id);
        setDiscussions(posts);

        // Fetch all users for profile navigation lookup
        try {
          const users = await UserService.fetchAllUsers();
          setAllUsers(users);
        } catch (e) {
          console.warn("Failed to fetch all users for lookup", e);
        }

        setLoading(false);
      }
    };
    loadPosts();
  }, [tribe?.id]);

  useEffect(() => {
    if (!tribe.id) return;
    const joined = isTribeMember(tribe.id, tribe.ownerId);
    console.log('[TribeGroupScreen] Member check:', {
      tribeId: tribe.id,
      joined
    });
    setIsJoined(joined);

    // Fetch current user's group-scoped role from the membership subcollection
    const fetchMyGroupRole = async () => {
      const uid = userProfile?.id || firebaseUser?.uid;
      if (!uid) return;
      try {
        const { getDbSafe } = await import('../services/firebaseConfig');
        const { doc, getDoc } = await import('firebase/firestore');
        const db = await getDbSafe();
        const memberDoc = await getDoc(doc(db, 'tribes', tribe.id, 'members', String(uid)));
        if (memberDoc.exists()) {
          setMyGroupRole(memberDoc.data().role || 'member');
        } else {
          setMyGroupRole('member');
          // Phase 3: check for existing pending join request on load
          try {
            const { doc: fsDoc, getDoc: fsGetDoc } = await import('firebase/firestore');
            const tribeSnap = await fsGetDoc(fsDoc(db, 'tribes', tribe.id));
            if (tribeSnap.exists() && tribeSnap.data().requiresApproval) {
              const reqDoc = await fsGetDoc(fsDoc(db, 'tribes', tribe.id, 'join_requests', String(uid)));
              if (reqDoc.exists() && reqDoc.data().status === 'pending') {
                setJoinRequestPending(true);
              }
            }
          } catch {}
        }
      } catch (e) {
        console.warn('[TribeGroupScreen] Could not fetch group role:', e);
      }
    };
    fetchMyGroupRole();
  }, [tribe.id, tribe.ownerId, isTribeMember, userProfile?.id, userProfile?.originalId, firebaseUser?.uid, isOwner]);

  // Owner badge: reconcile the pending-requests count from the actual
  // join_requests docs. The pendingMemberCount counter can be 0/stale (it isn't
  // always incremented), so derive the real count for the "Join Requests" badge.
  useEffect(() => {
    if (!isOwner || !(tribe.requiresApproval || tribe.isUniversityTribe || tribe.tribeType === 'university')) return;
    let cancelled = false;
    (async () => {
      try {
        const pending = await GovernanceService.getJoinRequests(tribe.id);
        if (!cancelled) setPendingRequestCount(pending.length);
      } catch (e) {
        console.warn('[TribeGroupScreen] Could not load pending join requests:', e);
      }
    })();
    return () => { cancelled = true; };
  }, [isOwner, tribe.requiresApproval, tribe.isUniversityTribe, tribe.tribeType, tribe.id]);

  // When ownership is passed (via the global PassOwnership modal), reflect the
  // new owner locally so the FORMER owner is immediately treated as a regular
  // member — owner-only controls (Settings, Pass Ownership, Join Requests, etc.)
  // disappear without needing a reload.
  useEffect(() => {
    const handleTransferred = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (!detail || detail.entityType !== 'tribe' || String(detail.entityId) !== String(tribe.id)) return;
      const newOwnerId = String(detail.newOwnerId || '');
      setTribe((prev: any) => ({ ...prev, ownerId: newOwnerId }));
      if (newOwnerId) {
        UserService.fetchUserById(newOwnerId).then((u) => { if (u) setOwnerInfo(u); }).catch(() => {});
      }
    };
    window.addEventListener('ownershipTransferred', handleTransferred);
    return () => window.removeEventListener('ownershipTransferred', handleTransferred);
  }, [tribe.id]);

  // Read + persist per-tribe notification preference
  useEffect(() => {
    const pref = (userProfile as any)?.tribeNotifications?.[tribe.id];
    setNotificationsEnabled(pref !== undefined ? pref : true);
  }, [tribe.id, (userProfile as any)?.tribeNotifications]);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showShareGroup, setShowShareGroup] = useState(false);
  const [selectedYapToShare, setSelectedYapToShare] = useState<any>(null);
  const { 
    handleInteraction, 
    isPostLiked, 
    getPostLikeCount 
  } = useYapInteractions();

  const handleBlockUser = (userName: string) => {
    toast.error(`Blocked ${userName}`, {
      description: 'You will no longer see yaps from this user.',
    });
  };

  const handleCopyLink = (postId: string) => {
    toast.success('Link copied to clipboard!');
  };

  const handleViewProfile = (userName: string) => {
    toast.info(`Opening ${userName}'s profile...`);
  };

  const handleReportPost = async (post: any) => {
    const reason = window.prompt('Why are you reporting this post? (Harassment, Spam, etc.)');
    if (!reason || reason.trim().length < 4) {
      if (reason !== null) toast.error('A valid reason is required to report.');
      return;
    }
    try {
      const { reportToWatchers } = await import('../services/WatcherService');
      await reportToWatchers(
        String(currentUserId || ''),
        String(post.id),
        'tribe_post',
        reason.trim(),
        {
          targetUserId: String(post.userId || post.authorId || ''),
          authorUsername: post.author,
          content: post.content,
          tribeId: tribe?.id,
        }
      );
      toast.success('Post reported', { description: 'Our moderators will review this content.' });
    } catch (err) {
      toast.error('Failed to submit report.');
    }
  };

  const handleYapClick = (yap: any) => {
    window.dispatchEvent(new CustomEvent('navigateToYapDetail', { detail: yap }));
  };

  const handleAddImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (newPostImages.length >= 2) {
      toast.error('Maximum 2 images allowed');
      return;
    }

    if (!firebaseUser?.uid) {
      toast.error("You must be logged in to upload images.");
      return;
    }

    try {
      const url = await TribeService.uploadPostImage('tribe', tribe.id, firebaseUser.uid, file);
      setNewPostImages(prev => [...prev, url]);
      toast.success('Image uploaded!');
    } catch (error) {
      console.error("Image upload failed:", error);
      toast.error('Failed to upload image. Please try again.');
    }
  };

  const handleTriggerUpload = () => {
    imageInputRef.current?.click();
  };

  const handleRemoveImage = (index: number) => {
    setNewPostImages(newPostImages.filter((_, i) => i !== index));
  };

  const handleCreatePost = async () => {
    if (newPostContent.trim()) {
      if (exceedsYapLimit(newPostContent, userProfile)) {
        toast.error(`Yap is too long. Maximum ${YAP_MAX_CHARACTERS} characters allowed.`);
        return;
      }

      try {
        const newPost = await TribeService.createPost(tribe.id, newPostContent, newPostImages);

        // Add new yap to the top of discussions
        setDiscussions([newPost, ...discussions]);

        toast.success('Yap created!', {
          description: `Your yap is now live in the tribe`,
        });
        setNewPostContent('');
        setNewPostImages([]);
        setShowNewPostModal(false);
      } catch (error: any) {
        toast.error("Failed to create post. Please try again.");
        console.error("Error creating post:", error);
      }
    }
  };

  const handleDeletePost = async (postId: string) => {
    if (!window.confirm("Are you sure you want to delete this post?")) {
        return;
    }

    try {
        await TribeService.deletePost(tribe.id, postId);
        setDiscussions(prev => prev.filter(p => p.id !== postId));
        toast.success("Post deleted");
    } catch (error) {
        console.error("Failed to delete post:", error);
        toast.error("Failed to delete post");
    }
  };

  // Filter discussions based on search query, and drop posts from blocked users.
  const filteredDiscussions = discussions.filter((post) =>
    !isBlockedContent(post) && (
      post.content.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.author.toLowerCase().includes(searchQuery.toLowerCase())
    )
  );

  const handleJoinToggle = async () => {
    if (isJoined) {
        // LEAVING — rejoining is immediate; there is no cooldown.
        try {
            await leaveTribe(tribe.id);
            setIsJoined(false);
            toast.success(`Left ${tribe.name}!`);
        } catch (error) {
            toast.error("Failed to leave tribe");
        }
    } else {
        // JOINING
        if (tribe.requiresApproval) {
            // Show email prompt — submission happens in handleSubmitJoinRequest
            setJoinEmail('');
            setEmailError('');
            setShowEmailPrompt(true);
        } else {
            // Standard auto-join for non-approval tribes — unchanged
            try {
                await joinTribe(tribe.id);
                setIsJoined(true);
            } catch (error) {
                console.error("Join tribe failed:", error);
            }
        }
    }
  };

  const handleSubmitJoinRequest = async () => {
    const trimmedEmail = joinEmail.trim();
    if (!trimmedEmail) {
        setEmailError('Please enter your school email address');
        return;
    }
    // Basic format check: must contain @ and a dot after the @
    const atIndex = trimmedEmail.indexOf('@');
    if (atIndex < 1 || !trimmedEmail.slice(atIndex + 1).includes('.')) {
        setEmailError('Please enter a valid email address');
        return;
    }
    setEmailError('');
    setRequestSubmitting(true);
    try {
        await GovernanceService.createJoinRequest(
            String(currentUserId),
            tribe.id,
            null,
            trimmedEmail
        );
        setShowEmailPrompt(false);
        setJoinRequestPending(true);
        toast.success('Request sent!', { description: 'The tribe owner will review your request.' });
    } catch (error: any) {
        toast.error(error.message || 'Failed to submit join request');
    } finally {
        setRequestSubmitting(false);
    }
  };

  return (
    <div
      className="flex flex-col h-screen bg-light-pattern dark:bg-[#0D0D0D] overflow-hidden"
      style={{ height: '100dvh' }}
    >

      {/* Main Scrollable Content */}
      <div className="flex-1 min-h-0 overflow-y-auto relative w-full scrollbar-hide">
         <div className="fixed inset-0 pointer-events-none z-0">
           <ParallaxBackground speed={0.05} />
         </div>

         {/* Content Container */}
         <div className="relative z-10 w-full max-w-2xl mx-auto min-h-screen pb-20">
           
           {/* 1. Header & Banner Section (Scrolls Away) */}
           <div className="relative shadow-sm border-b border-black/10 dark:border-white/10 overflow-hidden">
             {/* School logo / University sticker — university tribes only, top-right
                 corner of banner. Hidden on mobile (<768px) so it doesn't crowd the
                 title; shown on tablet/desktop. */}
             {tribe.tribeType === 'university' && tribe.schoolLogoUrl && (
               <img
                 src={tribe.schoolLogoUrl}
                 alt={`${tribe.schoolName || tribe.name} logo`}
                 className="hidden md:block"
                 style={{
                   position: 'absolute',
                   top: 12,
                   right: 12,
                   width: 70,
                   height: 'auto',
                   maxHeight: 70,
                   transform: 'rotate(14deg)',
                   filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.7)) drop-shadow(0 1px 3px rgba(0,0,0,0.5))',
                   objectFit: 'contain',
                   zIndex: 25,
                   pointerEvents: 'none',
                 }}
               />
             )}
             {/* Background Image logic matched to original */}
             <div className="absolute inset-0 z-0">
               {(tribe.bannerUrl || tribe.image || tribe.bannerImage) ? (
                 <img
                   src={tribe.bannerUrl || tribe.image || tribe.bannerImage}
                   alt={tribe.name}
                   className="w-full h-full object-cover opacity-100"
                 />
               ) : (
                 <div
                   className="w-full h-full"
                   style={{ background: getGroupGradient(tribe.id) }}
                 />
               )}
               <div 
                 className="absolute inset-0 backdrop-blur-[2px]" 
                 style={{ background: 'radial-gradient(ellipse at center, rgba(0,0,0,0.2) 20%, rgba(0,0,0,0.85) 100%)' }}
               />
             </div>

             {/* Header Content */}
             <div 
                className="relative z-10 px-4 pb-4 w-full"
                style={{ paddingTop: 'max(40px, calc(env(safe-area-inset-top) + 20px))' }}
             >
               <div className="flex justify-between items-start gap-4 mb-3">
                 <div className="flex-1 min-w-0">
                    <h1 
                      className="text-2xl font-bold text-white flex items-center gap-3 drop-shadow-lg leading-tight"
                      style={{ textShadow: '1px 0px 0px #000, -1px 0px 0px #000, 0px 1px 0px #000, 0px -1px 0px #000, 1px 1px 0px #000, -1px -1px 0px #000, 1px -1px 0px #000, -1px 1px 0px #000, 1px 1px 3px rgba(0,0,0,0.8)' }}
                    >
                      <motion.button
                        whileTap={{ scale: 0.95 }}
                        onClick={onBack}
                        className="p-2 mr-1 rounded-full bg-black/30 backdrop-blur-lg flex items-center justify-center hover:bg-black/50 transition-colors border border-white/10 shrink-0"
                        aria-label="Go back"
                      >
                        <ArrowLeft className="w-5 h-5 text-white" />
                      </motion.button>

                      <span 
                        className={`${isTitleExpanded ? '' : 'truncate'} cursor-pointer hover:opacity-80 transition-opacity whitespace-pre-wrap`}
                        onClick={() => setIsTitleExpanded(!isTitleExpanded)}
                      >
                        {tribe.name}
                      </span>
                      {isOwner && (
                        <motion.button
                          whileTap={{ scale: 0.9 }}
                          onClick={() => setShowEditModal(true)}
                          className="p-1.5 rounded-full bg-white/10 hover:bg-white/20 transition-colors border border-white/20 shadow-[0_2px_4px_rgba(0,0,0,0.5)] backdrop-blur-md shrink-0"
                        >
                          <Settings className="w-4 h-4 text-white drop-shadow-md" />
                        </motion.button>
                      )}
                    </h1>
                    
                    <div 
                      className="flex items-center gap-3 text-white/90 text-xs font-medium mt-1 drop-shadow-md"
                      style={{ textShadow: '1px 0px 0px #000, -1px 0px 0px #000, 0px 1px 0px #000, 0px -1px 0px #000, 1px 1px 0px #000, -1px -1px 0px #000, 1px -1px 0px #000, -1px 1px 0px #000' }}
                    >
                      <button onClick={() => setShowMembersModal(true)} className="bg-black/30 rounded-full px-2 py-0.5 backdrop-blur-md flex items-center gap-1 border border-white/10 hover:bg-black/50 transition-colors">
                        <Users className="w-3 h-3" />
                         {formatMemberCount(tribe.memberCount || 1)}
                      </button>
                      {(tribe.tribeType === 'university' ? tribe.schoolName : ownerInfo?.name) && (
                          <span className="text-xs opacity-90 flex items-center gap-1">
                              {tribe.tribeType !== 'university' && 'by '}
                              <span className="font-bold text-white max-w-[160px] truncate">
                                  {tribe.tribeType === 'university' ? tribe.schoolName : ownerInfo!.name}
                              </span>
                          </span>
                      )}
                    </div>

                 </div>

                 {/* Top Right Logo - Hidden for university tribes (they use the absolute positioned schoolLogo sticker instead) */}
                 {tribe.image && tribe.tribeType !== 'university' && (
                   <div className="shrink-0 pt-2 pr-2" style={{ transform: 'rotate(12deg)' }}>
                     <img
                       src={tribe.image}
                       alt="Community Logo"
                       className="h-16 w-auto md:h-20 md:w-auto max-w-[120px] object-contain drop-shadow-[0_10px_15px_rgba(0,0,0,0.8)]"
                     />
                   </div>
                 )}

                 {/* University school logo — inline on mobile. The rotated sticker
                     above is desktop-only (hidden md:block), so without this the
                     logo never showed on phones. */}
                 {tribe.tribeType === 'university' && tribe.schoolLogoUrl && (
                   <div className="shrink-0 pt-2 pr-2 md:hidden" style={{ transform: 'rotate(12deg)' }}>
                     <img
                       src={tribe.schoolLogoUrl}
                       alt={`${tribe.schoolName || tribe.name} logo`}
                       className="h-16 w-auto max-w-[120px] object-contain drop-shadow-[0_10px_15px_rgba(0,0,0,0.8)]"
                     />
                   </div>
                 )}
               </div>

                {/* Description with 'Show More' Toggle and Outline */}
                {tribe.description && (
                  <div className="mt-3 mb-1">
                    <p 
                      className="text-xs text-white leading-relaxed"
                      style={{ 
                        textShadow: '1px 0px 0px #000, -1px 0px 0px #000, 0px 1px 0px #000, 0px -1px 0px #000, 1px 1px 0px #000, -1px -1px 0px #000, 1px -1px 0px #000, -1px 1px 0px #000, 1px 1px 3px rgba(0,0,0,0.8)' // Heavy black outline
                      }}
                    >
                      {tribe.description}
                    </p>
                  </div>
                )}

                  {/* Invite banner — shown for non-members arriving via an invite
                      link. Validated server-side; an invalid/expired token degrades
                      gracefully to a "request to join" message (never blocks). */}
                  {!isOwner && initialInviteCode && !isJoined && !joinRequestPending && inviteCheck && (
                    <div className="mb-3 p-3 rounded-xl bg-blue-500/12 border border-blue-400/30">
                      <p className="text-sm text-white font-medium">
                        {inviteCheck.status === 'checking'
                          ? `Checking your invite to ${inviteCheck.tribeName}…`
                          : inviteCheck.status === 'valid'
                            ? `You've been invited to join ${inviteCheck.tribeName}.`
                            : `This invite link is no longer valid. You can still request to join ${inviteCheck.tribeName}.`}
                      </p>
                      {inviteCheck.status !== 'checking' && (
                        <Button
                          onClick={() => { if (inviteCheck.requiresApproval) setShowEmailPrompt(true); else handleJoinToggle(); }}
                          className="mt-2 h-9 px-4 bg-linear-to-r from-[#F39C12] to-[#E74C3C] dark:from-[#4478ea] dark:to-[#2d1ef1] hover:opacity-90 border-none text-white rounded-xl font-bold text-sm"
                        >
                          {inviteCheck.requiresApproval ? 'Request to Join' : 'Join Tribe'}
                        </Button>
                      )}
                    </div>
                  )}

                  {/* Main Action Buttons */}
                <div className="flex gap-2">
                  {!isOwner && (
                    <div className="flex-1 flex flex-col gap-2">
                      {isJoined ? (
                        <Button
                          onClick={handleJoinToggle}
                          className="flex-1 h-10 bg-green-500 hover:bg-green-600 border-none text-white rounded-xl font-bold shadow-lg transition-all"
                        >
                          ✓ Joined
                        </Button>
                      ) : joinRequestPending ? (
                        <Button
                          disabled
                          className="flex-1 h-10 bg-amber-500/20 border border-amber-500/40 text-amber-600 dark:text-amber-400 rounded-xl font-bold shadow-sm cursor-not-allowed"
                        >
                          <Clock className="w-4 h-4 mr-2" />
                          Request Pending
                        </Button>
                      ) : (tribe.requiresApproval || tribe.tribeType === 'university' || tribe.isUniversityTribe) ? (
                        <>
                          <Button
                            onClick={() => setShowEmailPrompt(prev => !prev)}
                            className="flex-1 h-10 bg-linear-to-r from-[#F39C12] to-[#E74C3C] dark:from-[#4478ea] dark:to-[#2d1ef1] hover:opacity-90 border-none text-white rounded-xl font-bold shadow-lg transition-all"
                          >
                            <GraduationCap className="w-4 h-4 mr-2" />
                            Request to Join
                          </Button>
                          {/* School email input (expands inline) */}
                          <AnimatePresence>
                            {showEmailPrompt && (
                              <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                                className="overflow-hidden"
                              >
                                <div className="flex gap-2 pt-1">
                                  <input
                                    type="email"
                                    value={joinEmail}
                                    onChange={e => setJoinEmail(e.target.value)}
                                    placeholder={(tribe.tribeType === 'university' || tribe.isUniversityTribe) ? "Your school email (e.g. you@howard.edu)" : "Enter your email"}
                                    className="flex-1 h-9 px-3 text-xs rounded-xl bg-black/10 dark:bg-white/10 text-black dark:text-white placeholder:text-black/40 dark:placeholder:text-white/40 border border-black/10 dark:border-white/15 focus:outline-none focus:border-[#F39C12] dark:focus:border-[#4478ea]"
                                    onKeyDown={e => { if (e.key === 'Enter') handleSubmitJoinRequest(); }}
                                  />
                                  <Button
                                    onClick={handleSubmitJoinRequest}
                                    disabled={requestSubmitting || !joinEmail.trim()}
                                    className="h-9 px-3 text-xs bg-linear-to-r from-[#F39C12] to-[#E74C3C] dark:from-[#4478ea] dark:to-[#2d1ef1] text-white rounded-xl font-bold disabled:opacity-50"
                                  >
                                    Send
                                  </Button>
                                </div>
                                {emailError && <p className="text-[10px] text-red-500 mt-1 pl-1">{emailError}</p>}
                                <p className="text-[10px] text-white/60 mt-1 pl-1">
                                  {(tribe.tribeType === 'university' || tribe.isUniversityTribe) ? "Your email helps the owner verify you're a student." : "The owner will review your request."}
                                </p>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </>
                      ) : (
                        <Button
                          onClick={handleJoinToggle}
                          disabled={tribe.isPublic === false}
                          className={`flex-1 h-10 ${
                            tribe.isPublic === false
                              ? 'bg-white/10 text-white/30 cursor-not-allowed border border-white/10'
                              : 'bg-linear-to-r from-[#F39C12] to-[#E74C3C] dark:from-[#4478ea] dark:to-[#2d1ef1] hover:opacity-90 border-none'
                          } text-white rounded-xl font-bold shadow-lg transition-all`}
                        >
                          {tribe.isPublic === false ? (
                            <div className="flex items-center gap-1.5 text-[11px] leading-tight">
                              <Lock className="w-3.5 h-3.5 shrink-0" />
                              <span>Invite Required to Join</span>
                            </div>
                          ) : 'Join Tribe'}
                        </Button>
                      )}
                    </div>
                  )}

                  {isOwner && (
                    <Button
                      onClick={() => setShowEditModal(true)}
                      className="flex-1 h-10 bg-white/20 backdrop-blur-md border border-white/20 hover:bg-white/30 text-white rounded-xl font-bold shadow-lg transition-all"
                    >
                      <div className="flex items-center gap-2"><Settings className="w-4 h-4" /> Settings</div>
                    </Button>
                  )}

                  {/* The owner-only invite button that used to sit here is gone: the
                      share button at the end of this row carries the same Share2
                      icon, does everything it did, and is open to every viewer. */}
                  <Button
                    onClick={() => setShowRulesModal(true)}
                    className="w-10 h-10 bg-white/20 backdrop-blur-md text-white hover:bg-white/30 border border-white/20 rounded-xl p-0 shadow-lg shrink-0 flex items-center justify-center"
                    title="Community Rules"
                  >
                    <FileText className="w-5 h-5 drop-shadow-md" />
                  </Button>
                  <Button
                    onClick={async () => {
                      if (!isJoined) {
                          toast.error("Members only", { description: "Join this tribe to enable notifications" });
                          return;
                      }
                      const newState = !notificationsEnabled;
                      setNotificationsEnabled(newState);
                      if (newState) {
                          toast.success("Notifications Enabled");
                      } else {
                          toast.info("Notifications Disabled");
                      }
                      const uid = userProfile?.id || firebaseUser?.uid;
                      if (!uid) return;
                      try {
                        const { getDbSafe } = await import('../services/firebaseConfig');
                        const { doc, updateDoc } = await import('firebase/firestore');
                        const db = await getDbSafe();
                        await updateDoc(doc(db, 'users', String(uid)), {
                          [`tribeNotifications.${tribe.id}`]: newState,
                        });
                      } catch (e) {
                        console.warn('[TribeGroupScreen] Failed to save notification pref:', e);
                      }
                    }}
                    className={`w-10 h-10 ${notificationsEnabled
                      ? 'bg-green-500 hover:bg-green-600 text-white border-none'
                      : 'bg-white/20 backdrop-blur-md text-white hover:bg-white/30 border border-white/20'
                      } rounded-xl p-0 shadow-lg shrink-0 flex items-center justify-center`}
                  >
                    {notificationsEnabled ? <BellOff className="w-5 h-5 drop-shadow-md" /> : <Bell className="w-5 h-5 drop-shadow-md" />}
                  </Button>
                  {/* Share sits in the header, available to anyone looking at the
                      tribe. The invite link further down is owner-only because it
                      lives in the owner settings block; this is the path for
                      everyone else. */}
                  <Button
                    onClick={(e) => { e.stopPropagation(); setShowShareGroup(true); }}
                    className="w-10 h-10 bg-white/20 backdrop-blur-md text-white hover:bg-white/30 border border-white/20 rounded-xl p-0 shadow-lg shrink-0 flex items-center justify-center"
                    aria-label="Share this tribe"
                  >
                    <Share2 className="w-5 h-5 drop-shadow-md" />
                  </Button>
                </div>
             </div>
           </div>

           {/* Tags — full-width row below banner/actions */}
           {((tribe.tags ?? tribe.identities) ?? []).length > 0 && (
             <div className="flex flex-wrap gap-1.5 px-4 py-3 bg-[#fdfcf0]/95 dark:bg-[#0D0D0D]/95 border-b border-black/5 dark:border-white/5">
               {((tribe.tags ?? tribe.identities) as string[]).map((tag: string) => (
                 <span
                   key={tag}
                   className="text-[11px] font-bold text-white rounded-full whitespace-nowrap"
                   style={{ padding: '4px 12px', background: 'linear-gradient(135deg, #4478ea 0%, #2d1ef1 100%)' }}
                 >
                   {tag}
                 </span>
               ))}
             </div>
           )}

           {/* 2. Sticky Search Bar Section */}
           <div className="sticky top-0 z-40 px-4 py-3 bg-[#fdfcf0]/95 dark:bg-[#0D0D0D]/95 backdrop-blur-md border-b border-black/5 dark:border-white/5 shadow-sm transition-colors duration-300">
             <div className="flex gap-2">
               <div className="relative flex-1">
                 <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#666666] dark:text-[#999999]" />
                 <Input
                   value={searchQuery}
                   onChange={(e) => setSearchQuery(e.target.value)}
                   placeholder="Search yaps..."
                   className="w-full pl-9 h-10 bg-black/5 dark:bg-white/10 border-none text-black dark:text-white placeholder:text-black/50 dark:placeholder:text-white/50 rounded-xl focus-visible:ring-1 focus-visible:ring-[#F39C12] dark:focus-visible:ring-[#4478ea]"
                 />
               </div>
               <Button
                  onClick={() => {
                      if (!isJoined) {
                          toast.error("Members only", { description: "You must join this tribe to post." });
                          return;
                      }
                      setShowNewPostModal(true);
                  }}
                  className={`h-10 px-4 text-white rounded-xl font-bold shadow-sm transition-all bg-linear-to-r from-[#F39C12] to-[#E74C3C] dark:from-[#4478ea] dark:to-[#2d1ef1] ${
                    !isJoined 
                      ? 'opacity-50 grayscale cursor-not-allowed' 
                      : 'hover:opacity-90'
                  }`}
                >
                  <Plus className="w-5 h-5" />
                </Button>
             </div>
           </div>

           {/* 3. Posts List */}
           <div className="p-4 space-y-4">
             {/* Owner Tools */}
            {tribe.ownerId === userProfile?.id && (
              <div className="mb-2 p-4 rounded-xl bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-900/30 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-red-700 dark:text-red-400 font-medium flex items-center gap-2">
                      Owner Tools
                      {tribe.tribeType === 'university' && tribe.hiddenOwner && (
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-[#FFD700]/20 text-[#b8860b] dark:text-[#FFD700] uppercase tracking-wider">Hidden</span>
                      )}
                    </h4>
                    <p className="text-xs text-red-600/80 dark:text-red-400/70">
                      {tribe.tribeType === 'university' && tribe.hiddenOwner
                        ? 'Transfer ownership to reveal the owner — first transfer has no cooldown'
                        : 'Manage tribe ownership'}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      onClick={() => {
                        window.dispatchEvent(new CustomEvent('openPassOwnershipModal', {
                          detail: {
                            entityType: 'tribe',
                            entityId: tribe.id,
                            lastTransferAt: tribe.lastOwnershipTransferAt
                          }
                        }));
                      }}
                      className="bg-white dark:bg-transparent border-red-200 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 h-9"
                    >
                      Pass Ownership
                    </Button>
                  </div>
                </div>

                {/* Invite link — every tribe, not just university ones. University
                    tribes get their code at creation; any other tribe's owner
                    generates one on demand here. Both produce the same
                    /tribes/:id?invite=CODE link and land on TribeInvitePage. */}
                <div>
                  <h4 className="text-red-700 dark:text-red-400 font-medium flex items-center gap-2 mb-1.5">
                    <Link className="w-4 h-4" /> Invite Link
                  </h4>
                  {tribe.inviteCode ? (() => {
                    const inviteLink = buildTribeInviteLink(tribe.id, tribe.inviteCode);
                    return (
                      <div className="flex items-center gap-2 p-2 rounded-lg bg-black/5 dark:bg-white/5 border border-black/10 dark:border-white/10">
                        <p className="flex-1 text-xs font-mono text-black/60 dark:text-white/60 truncate">{inviteLink}</p>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(inviteLink).then(() => {
                              setInviteCopied(true);
                              setTimeout(() => setInviteCopied(false), 2000);
                            });
                          }}
                          className="shrink-0 p-1.5 rounded-md bg-black/10 dark:bg-white/10 hover:bg-blue-500/10 transition-colors"
                        >
                          {inviteCopied
                            ? <Check className="w-3.5 h-3.5 text-blue-500" />
                            : <Copy className="w-3.5 h-3.5 text-black/50 dark:text-white/50" />}
                        </button>
                      </div>
                    );
                  })() : (
                    <div className="flex items-center gap-2">
                      <p className="flex-1 text-xs text-black/50 dark:text-white/50">
                        Create a shareable link anyone can open to see this tribe and join.
                      </p>
                      <Button
                        variant="outline"
                        disabled={generatingInvite}
                        onClick={handleGenerateInviteLink}
                        className="h-9 shrink-0 bg-white dark:bg-transparent border-blue-200 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                      >
                        {generatingInvite ? 'Generating...' : 'Generate Link'}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Phase 3: Join Requests — owner only, approval-gated tribes only */}
            {isOwner && (tribe.requiresApproval || tribe.isUniversityTribe || tribe.tribeType === 'university') && (
              <div className="mb-2 p-4 rounded-xl bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-900/30">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-blue-700 dark:text-blue-400 font-medium flex items-center gap-2">
                      Join Requests
                      {pendingRequestCount > 0 && (
                        <span className="w-5 h-5 rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center font-bold">
                          {pendingRequestCount > 9 ? '9+' : pendingRequestCount}
                        </span>
                      )}
                    </h4>
                    <p className="text-xs text-blue-600/80 dark:text-blue-400/70">Review and approve member requests</p>
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => setShowJoinRequestsModal(true)}
                    className="bg-white dark:bg-transparent border-blue-200 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 h-9"
                  >
                    View Requests
                  </Button>
                </div>
              </div>
            )}
            
            {loading ? (
              <p className="text-center text-[#666666] dark:text-[#E5E5E5]">Loading discussion...</p>
            ) : (filteredDiscussions.length === 0 || !filteredDiscussions) ? (
              <div className="flex flex-col items-center justify-center py-20 text-center px-6">
                <div className="w-20 h-20 rounded-full bg-black/5 dark:bg-white/5 flex items-center justify-center mb-6">
                  <MessageCircle className="w-10 h-10 text-[#999999]/40" />
                </div>
                <p className="text-[#666666] dark:text-[#E5E5E5] text-lg font-medium">
                  No Yaps yet
                </p>
                <p className="text-[#999999] text-sm mt-1">Be the first to start the discussion!</p>
              </div>
            ) : (
              filteredDiscussions.map((post) => (
                <PressableCard
                  key={post.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  onClick={() => handleYapClick(post)}
                  className="p-4 rounded-2xl bg-[#F5F5F5] dark:bg-white/5 border border-black/5 dark:border-white/10 hover:bg-[#FAF8F5] dark:hover:bg-white/10 transition-colors cursor-pointer card-shadow"
                >
                  {/* Author Info */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <Avatar className={`w-10 h-10 ${post.rank ? getRankRingParams(post.rank).ringClass : 'border-2 border-[#F39C12] dark:border-[#4478ea]'}`}>
                        <AvatarImage src={post.avatar} />
                        <AvatarFallback>{post.author[0]}</AvatarFallback>
                      </Avatar>
                      <div className="min-w-0">
                        <h4 className="text-black dark:text-white flex items-center gap-1.5 leading-tight">
                          <span className="truncate">{post.author}</span>
                          {(post.authorCountryFlag || post.country) && (
                            <FlagImage
                              countryName={post.authorCountryFlag || post.country}
                              size="w80"
                              className="w-3.5 h-2.5 rounded shrink-0 mb-0.5"
                            />
                          )}
                          <RankBadge rank={post.rank || (post as any).authorRank} size={18} />
                        </h4>
                        <p className="text-xs text-[#666666] dark:text-[#E5E5E5] mt-0.5">{post.time}</p>
                        {post.userId && String(post.userId) !== String(currentUserId) && isFriend(String(post.userId)) && (
                          <div className="mt-0.5">
                            <FriendBadge />
                          </div>
                        )}
                      </div>
                    </div>

                    {/* 3-dot menu */}
                    {/* Shared menu. `group` is required at the type level on
                        this surface: Download's gate reads tribe privacy, which
                        lives on the tribe document rather than the post, and it
                        fails closed without it. Authors get Edit and Delete here
                        for the first time — the hand-written menu offered
                        neither. */}
                    <PostMenu
                      post={post as any}
                      surface="group"
                      collectionName="tribe_posts"
                      group={{
                        id: String(tribe?.id || ''),
                        requiresApproval: !!(tribe as any)?.requiresApproval,
                        isPublic: (tribe as any)?.isPublic,
                        isMember: isTribeMember(String(tribe?.id || ''), (tribe as any)?.ownerId),
                      }}
                      onChanged={() => window.dispatchEvent(new CustomEvent('yapUpdated'))}
                    />
                  </div>

                  {/* Content */}
                  <ExpandableText
                    text={post.content}
                    className="text-[#333333] dark:text-[#E5E5E5] mb-3 leading-relaxed block"
                  />

                  {/* Poll. Votes and tallies hang off the post's own collection,
                      so tribe posts read and write under tribe_posts, not yaps. */}
                  {(post as any).poll?.options?.length > 0 && (
                    <div className="mb-3">
                      <PollCard
                        yapId={String(post.id)}
                        poll={(post as any).poll}
                        collectionName="tribe_posts"
                      />
                    </div>
                  )}

                  {/* Topic label (forum cross-post) — below the text, above media */}
                  {post.forumTopic?.forumId && (
                    <div className="mb-3">
                      <YapTopicLabel forumId={post.forumTopic.forumId} forumName={post.forumTopic.forumName} />
                    </div>
                  )}

                  {/* Images — shared compact grid (clipped, Twitter-style) */}
                  {(post.images?.length || post.image) ? (
                    <div className="mb-3">
                      <YapImageGrid images={post.images?.length ? post.images : [post.image!]} />
                    </div>
                  ) : null}

                  {/* Attached video (optional trim window) */}
                  {(post as any).videoUrl && (
                    <div className="mb-3">
                      <YapVideoPlayer
                        videoUrl={(post as any).videoUrl}
                        trimStart={(post as any).videoTrimStart}
                        trimEnd={(post as any).videoTrimEnd}
                        videoWidth={(post as any).videoWidth}
                        videoHeight={(post as any).videoHeight}
                        authorName={post.author}
                      />
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex items-center gap-6">
                    {/* No like and no reply on a tribe post — forum posts keep
                        both. Sharing stays: passing a post along is not the same
                        as reacting to it inside the tribe.

                        The reply control here never did anything anyway; its
                        handler checked membership and then fell through to a
                        comment that said the count was all it showed. */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!isJoined) {
                            toast.error("Members only", { description: "Join this tribe to share." });
                            return;
                        }
                        setSelectedYapToShare(post);
                        setShowShareModal(true);
                      }}
                      className={`flex items-center gap-2 transition-colors touch-clickable ${!isJoined ? 'opacity-50' : 'hover:text-[#F39C12] dark:hover:text-[#4478ea]'} text-[#666666] dark:text-[#E5E5E5]`}
                    >
                      <Share2 className="w-4 h-4" />
                    </button>
                  </div>
                </PressableCard>
              ))
            )}
           </div>
         </div>
      </div>

      {/* New Post Modal */}
      <AnimatePresence>
        {showNewPostModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
            onClick={() => setShowNewPostModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-lg bg-light-pattern rounded-3xl overflow-hidden shadow-2xl"
            >
              {/* Header */}
              <div className="p-4 border-b border-black/10 dark:border-white/10 bg-white/80 dark:bg-black/30 backdrop-blur-lg"
                style={{ paddingTop: 'max(60px, calc(env(safe-area-inset-top) + 24px))' }}>
                <div className="flex items-center justify-center mb-4 relative">
                  <h2 className="text-xl text-black dark:text-white">Create Tribe Post</h2>
                  <motion.button
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setShowNewPostModal(false)}
                    className="absolute right-0 text-[#666666] dark:text-[#E5E5E5] hover:text-black dark:hover:text-white"
                  >
                    <X className="w-6 h-6" />
                  </motion.button>
                </div>
              </div>

              <div className="p-6">
                {/* Info Banner */}
                <div className="mb-4 p-3 rounded-xl bg-[#F39C12]/10 dark:bg-[#4478ea]/10 border border-[#F39C12]/30 dark:border-[#4478ea]/30">
                  <p className="text-sm text-[#666666] dark:text-[#E5E5E5]">
                    ℹï¸ This yap will only be visible in this tribe
                  </p>
                </div>

                {/* Text Area */}
                <Textarea
                  value={newPostContent}
                  onChange={(e) => {
                    // 1000 characters, no limit for Legion
                    if (!exceedsYapLimit(e.target.value, userProfile)) {
                      setNewPostContent(e.target.value);
                    }
                  }}
                  placeholder="Share your thoughts..."
                  className="w-full min-h-[150px] bg-[#F5F5F5] dark:bg-[#2a2a2a] border-black/10 dark:border-white/20 text-black dark:text-white resize-none rounded-xl mb-4"
                />

                {/* @Mention Indicator while typing */}
                {newPostContent.match(/@[\w\.]+/g) && (
                  <motion.div 
                    initial={{ opacity: 0, y: -5 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-4 flex flex-wrap gap-1.5"
                  >
                    <span className="text-[10px] text-[#666666] dark:text-[#999999] uppercase font-bold flex items-center mr-1"> Mentioning: </span>
                    {Array.from(new Set(newPostContent.match(/@[\w\.]+/g))).map((mention, i) => (
                      <span 
                        key={i}
                        className="px-2 py-0.5 rounded-full bg-[#F39C12]/10 dark:bg-[#4478ea]/10 border border-[#F39C12]/20 dark:border-[#4478ea]/20 text-[#F39C12] dark:text-[#4478ea] text-[10px] font-bold"
                      >
                        {mention}
                      </span>
                    ))}
                  </motion.div>
                )}

                {/* Character Count */}
                <p className={`text-sm mb-4 ${yapCharLimit(userProfile) !== null && newPostContent.length >= YAP_MAX_CHARACTERS ? 'text-[#E74C3C] dark:text-[#2d1ef1]' : 'text-[#999999]'}`}>
                  {yapCharLimit(userProfile) === null ? `${newPostContent.length} characters` : `${newPostContent.length} / ${YAP_MAX_CHARACTERS} characters`}
                </p>

                {/* Image Counter */}
                {newPostImages.length > 0 && (
                  <p className="text-sm text-[#666666] dark:text-[#E5E5E5] mb-4">
                    📷 {newPostImages.length} / 2 images added
                  </p>
                )}

                <Button
                  onClick={handleTriggerUpload}
                  disabled={newPostImages.length >= 2}
                  className={`w-full h-12 rounded-xl mb-4 shadow-md transition-all ${newPostImages.length >= 2
                    ? 'bg-[#E5E5E5] dark:bg-[#2a2a2a] text-[#999999] cursor-not-allowed'
                    : 'bg-linear-to-r from-[#F39C12] to-[#E74C3C] dark:from-[#4478ea] dark:to-[#2d1ef1] hover:opacity-90 text-white font-bold'
                    }`}
                >
                  <Plus className="w-5 h-5 mr-2" />
                  {newPostImages.length >= 2 ? 'Maximum Images Added' : 'Add Photos'}
                </Button>
                <input
                  type="file"
                  ref={imageInputRef}
                  onChange={handleAddImage}
                  className="hidden"
                  accept="image/*"
                />

                {/* Display Added Images */}
                {newPostImages.map((image, index) => (
                  <div key={index} className="relative mb-4">
                    <img
                      src={image}
                      alt={`Added Image ${index + 1}`}
                      className="w-full h-auto rounded-xl shadow-lg border-2 border-white/10"
                    />
                    <motion.button
                      whileTap={{ scale: 0.95 }}
                      onClick={() => handleRemoveImage(index)}
                      className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-2 shadow-lg"
                    >
                      <X className="w-4 h-4" />
                    </motion.button>
                  </div>
                ))}

                {/* Actions */}
                <div className="flex gap-3">
                  <Button
                    onClick={() => setShowNewPostModal(false)}
                    variant="outline"
                    className="flex-1 h-12 bg-transparent border-2 border-[#2C3E50] text-[#2C3E50] dark:text-white hover:bg-[#2C3E50]/10 rounded-xl"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleCreatePost}
                    disabled={!newPostContent.trim()}
                    className={`flex-1 h-12 rounded-xl ${!newPostContent.trim()
                      ? 'bg-[#E5E5E5] dark:bg-[#2a2a2a] text-[#999999] cursor-not-allowed'
                      : 'bg-linear-to-r from-[#F39C12] to-[#E74C3C] dark:from-[#4478ea] dark:to-[#2d1ef1] hover:opacity-90 text-white'
                      }`}
                  >
                    Yap to Tribe
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Share the tribe itself. Outside the selectedYapToShare guard below —
          that one gates sharing a POST, which is a different thing. */}
      <ShareGroupModal
        isOpen={showShareGroup}
        onClose={() => setShowShareGroup(false)}
        group={{
          id: String(tribe.id),
          name: tribe.name,
          type: 'tribe',
          bannerUrl: tribe.bannerUrl || tribe.bannerImage || tribe.image || '',
          // An approval-gated tribe is not openly joinable, so the receiver
          // needs the invite path rather than the public one.
          isPublic: !(tribe.requiresApproval || tribe.isUniversityTribe || tribe.tribeType === 'university'),
          inviteCode: tribe.inviteCode,
        }}
      />

      {/* Share Yap Modal */}
      {selectedYapToShare && (
        <ShareYapModal
          isOpen={showShareModal}
          onClose={() => {
            setShowShareModal(false);
            setSelectedYapToShare(null);
          }}
          yap={selectedYapToShare}
        />
      )}
      <GroupSettingsModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        group={tribe}
        type="tribe"
        onUpdate={(updated: any) => setTribe((prev: any) => ({ ...prev, ...updated }))}
      />

      <CommunityRulesModal
        isOpen={showRulesModal}
        onClose={() => setShowRulesModal(false)}
        groupName={tribe.name}
        rules={tribe.rules || ''}
        type="tribe"
      />
      <GroupMembersModal
        isOpen={showMembersModal}
        onClose={() => setShowMembersModal(false)}
        groupId={tribe.id}
        type="tribe"
        hiddenOwner={tribe.hiddenOwner === true}
      />
      {/* Phase 3: Join Requests modal — owner only */}
      {isOwner && (tribe.requiresApproval || tribe.isUniversityTribe || tribe.tribeType === 'university') && (
        <JoinRequestsModal
          isOpen={showJoinRequestsModal}
          onClose={() => setShowJoinRequestsModal(false)}
          tribeId={tribe.id}
          tribeName={tribe.name || ''}
          ownerId={String(currentUserId)}
          onUpdate={async () => {
            try {
              const pending = await GovernanceService.getJoinRequests(tribe.id);
              // Keep the owner's pending badge accurate after an approve/deny
              // without waiting for a doc re-sync.
              setPendingRequestCount(pending.length);
            } catch (e) {
              console.error("Failed to update join requests count:", e);
            }
          }}
        />
      )}

      {/* School email prompt — shown when tapping Request to Join on approval-gated tribe */}
      <AnimatePresence>
        {showEmailPrompt && (
          <div
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-4"
            onClick={() => setShowEmailPrompt(false)}
          >
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 24 }}
              transition={{ duration: 0.2 }}
              className="w-full max-w-sm rounded-2xl shadow-2xl p-6 border border-black/10 dark:border-white/10 bg-[#fdfcf0] dark:bg-[#1a1a1a]"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-base font-bold text-black dark:text-white mb-1">Request to Join</h3>
              <p className="text-xs text-black/50 dark:text-white/50 mb-4 leading-relaxed">
                Enter your school email so the admin can verify your enrollment. This will not be checked automatically.
              </p>

              <label className="block text-sm font-bold text-black dark:text-white mb-2">School Email</label>
              <Input
                type="email"
                value={joinEmail}
                onChange={(e) => { setJoinEmail(e.target.value); setEmailError(''); }}
                placeholder="you@university.edu"
                autoFocus
                className="bg-zinc-100/50 dark:bg-black/40 border-black/10 dark:border-white/10 h-11 text-base rounded-xl"
              />
              {emailError && (
                <p className="text-xs text-red-500 mt-1.5">{emailError}</p>
              )}

              <div className="flex gap-2 mt-5">
                <Button
                  variant="ghost"
                  onClick={() => setShowEmailPrompt(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSubmitJoinRequest}
                  disabled={requestSubmitting}
                  className="flex-1 bg-linear-to-r from-[#F39C12] to-[#E74C3C] dark:from-[#4478ea] dark:to-[#2d1ef1] text-white border-none font-bold disabled:opacity-50"
                >
                  {requestSubmitting ? 'Sending…' : 'Send Request'}
                </Button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
